import { Fragment, useEffect, useMemo } from 'react';

import type { ExtractedStatement } from '../../model/extractedStatements';
import type { ReferenceNumbering } from '../../model/numbering';
import { computeSegments, segmentStarts } from '../../model/segments';

import type { InlineCitation } from '@/components/chat/bubbles/references/inlineCitations';
import {
  type MessageTextPiece,
  renderMessageTextPieces,
  splitMessageRun,
} from '@/components/chat/messageText/messageTextPieces';
import { StatementChip } from '@/components/chat/statements/StatementChip';
import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import type { ReferenceDetailsEntryPoint } from '@/util/analyticsEvents';
import { openMessageLink } from '@/util/messageLinks';
import { getPressProps } from '@/util/pressProps';
import { Sentry } from '@/util/sentry';

interface HighlightedMessageTextProps {
  messageSid: string;
  body: string;
  extractedStatements: ExtractedStatement[];
  textClassName?: string;
  position: 'left' | 'right';
  /**
   * The statements' chip numbers from the parent bubble, which takes them
   * from the server's numbering. Passed in so every surface shares one
   * `computeReferenceNumbering` result.
   */
  numbering: ReferenceNumbering;
  /** The numbers whose reference has loaded and can be opened. A statement's
   * other numbers, such as the author's pending requests, draw as plain text. */
  openableNumbers?: ReadonlySet<number>;
  /** Opens the reference sheet at a statement's reference, saying whether
   * its chip or its words were tapped. */
  onOpenStatementReference?: (number: number, entryPoint: StatementEntryPoint) => void;
  /** Reference numbers to place after the words in the body that cite them,
   * such as a trial name or a pasted link. */
  inlineCitations?: readonly InlineCitation[];
  /** Opens the reference sheet at a number tapped in the text. */
  onOpenCitation?: (number: number) => void;
  /** Opens a link tapped in the text, so the bubble can report it. Opens it
   * unreported when omitted. */
  onOpenLink?: (href: string) => void;
}

/**
 * Renders the message body with each cited statement's source span
 * underlined and its `[N]` chips after the run. Tapping a chip opens that
 * reference, and tapping the underlined words opens the statement's first
 * reference. A statement with no reference stays plain prose.
 */
export function HighlightedMessageText({
  messageSid,
  body,
  extractedStatements,
  textClassName,
  position,
  numbering,
  openableNumbers = NO_NUMBERS,
  onOpenStatementReference = NO_OP,
  inlineCitations = NO_CITATIONS,
  onOpenCitation = NO_OP,
  onOpenLink = openMessageLink,
}: HighlightedMessageTextProps) {
  const result = useMemo(
    () => computeSegments(body, extractedStatements),
    [body, extractedStatements]
  );

  const starts = useMemo(() => segmentStarts(body, result.segments), [body, result.segments]);

  const pieceOptions = {
    position,
    textClassName,
    linkStyle: LINK_STYLE,
    onOpenLink,
    onOpenCitation,
  };

  useEffect(() => {
    if (result.fallbackUsed) {
      Sentry.addBreadcrumb({
        category: 'extracted-statements',
        message: 'Span exceeded body length; rendered plain body',
        level: 'warning',
        data: {
          messageSid,
          bodyLength: body.length,
          statementCount: extractedStatements.length,
        },
      });
    }

    if (result.droppedStatementIndices.length > 0) {
      Sentry.addBreadcrumb({
        category: 'extracted-statements',
        message: 'Overlapping spans dropped from inline marks',
        level: 'info',
        data: {
          messageSid,
          droppedStatementIndices: result.droppedStatementIndices,
        },
      });
    }
  }, [result, messageSid, body.length, extractedStatements.length]);

  return (
    <Text className={textClassName}>
      {result.segments.map((segment, segmentIndex) => {
        const pieces = splitMessageRun(segment.text, starts[segmentIndex], inlineCitations);

        const renderPlainPieces = () =>
          renderMessageTextPieces(pieces, `c-${segmentIndex}`, pieceOptions);

        if (segment.kind === 'plain') {
          return (
            <Text key={`p-${segmentIndex}`} className={textClassName}>
              {renderPlainPieces()}
            </Text>
          );
        }

        const numbersForStatement = numbering.byStatement.get(segment.statementIndex) ?? [];

        if (numbersForStatement.length === 0) {
          return (
            <Text key={`u-${segment.statementIndex}`} className={textClassName}>
              {renderPlainPieces()}
            </Text>
          );
        }

        const firstOpenable = numbersForStatement.find((number) => openableNumbers.has(number));

        const openStatement =
          firstOpenable === undefined
            ? undefined
            : () => onOpenStatementReference(firstOpenable, 'statement_text');

        return (
          <Text key={`h-${segment.statementIndex}`} className={textClassName}>
            {renderMessageTextPieces(
              withoutStatementNumbers(pieces, numbersForStatement),
              `r-${segmentIndex}`,
              {
                ...pieceOptions,
                renderRun: (text, key, onPress) => (
                  <HighlightedRun
                    key={key}
                    text={text}
                    textClassName={textClassName}
                    position={position}
                    onPress={onPress ?? openStatement}
                  />
                ),
              }
            )}
            {NON_BREAKING_SPACE}
            <ChipsForStatement
              statementIndex={segment.statementIndex}
              numbers={numbersForStatement}
              position={position}
              openableNumbers={openableNumbers}
              onOpen={(number) => onOpenStatementReference(number, 'statement_chip')}
            />
          </Text>
        );
      })}
    </Text>
  );
}

const NO_CITATIONS: readonly InlineCitation[] = [];

const NO_NUMBERS: ReadonlySet<number> = new Set();

const NO_OP = () => {};

type StatementEntryPoint = Extract<ReferenceDetailsEntryPoint, 'statement_chip' | 'statement_text'>;

const LINK_STYLE = { textDecorationLine: 'underline' as const };

/**
 * Drops a citation number inside an underlined run when the statement's own
 * chips, drawn at the end of the run, already carry it, so the reader does not
 * see the same number twice in one sentence. The cited words keep their
 * number: a statement can carry several references, and tapping words that
 * name one of them must open that one, not the statement's first.
 */
function withoutStatementNumbers(
  pieces: readonly MessageTextPiece[],
  numbersForStatement: readonly number[]
): MessageTextPiece[] {
  return pieces.filter(
    (piece) => piece.kind !== 'citation' || !numbersForStatement.includes(piece.number)
  );
}

interface ChipsForStatementProps {
  statementIndex: number;
  numbers: number[];
  position: 'left' | 'right';
  openableNumbers: ReadonlySet<number>;
  onOpen: (number: number) => void;
}

/**
 * Renders one `[N]` chip per reference on a highlighted run. A chip opens
 * its reference when that reference has loaded.
 *
 * Adjacent `[N][N+1]` chips render with a thin space between to keep
 * tap targets distinct on iOS — the spacing character is captured in
 * `CHIP_SEPARATOR` so it can be tuned without rewriting the renderer.
 */
function ChipsForStatement({
  statementIndex,
  numbers,
  position,
  openableNumbers,
  onOpen,
}: ChipsForStatementProps) {
  return (
    <>
      {numbers.map((number, index) => (
        <Fragment key={`n-${statementIndex}-${number}`}>
          {index > 0 ? CHIP_SEPARATOR : null}
          <StatementChip
            statementIndex={statementIndex}
            number={number}
            totalForStatement={numbers.length}
            position={position}
            onPress={openableNumbers.has(number) ? onOpen : undefined}
          />
        </Fragment>
      ))}{' '}
    </>
  );
}

/** U+2009 THIN SPACE between adjacent numbered chips so iOS doesn't
 * merge tap targets. Captured here so a future rendering tweak can
 * change the character (or swap to a `View` separator) in one place. */
const CHIP_SEPARATOR = ' ';

/** U+00A0 NO-BREAK SPACE between an underlined statement run and its
 * trailing chip. Forces the chip to wrap onto the next line WITH the
 * end of the underlined run rather than orphaning the chip on its
 * own line when the underline finishes near the bubble edge. */
const NON_BREAKING_SPACE = ' ';

interface HighlightedRunProps {
  text: string;
  textClassName?: string;
  position: 'left' | 'right';
  /** Opens a reference: the one the words cite, or the statement's first.
   * Omitted when the statement has nothing to open yet, and the run is then
   * underlined but not pressable. */
  onPress?: () => void;
}

function HighlightedRun({ text, textClassName, position, onPress }: HighlightedRunProps) {
  const { colors } = useColors();

  const underlineColor = position === 'right' ? colors.primary[200] : colors.outline[400];

  return (
    <Text
      style={{
        ...UNDERLINE_STYLE,
        ...(onPress ? CLICKABLE_STYLE : {}),
        textDecorationColor: underlineColor,
      }}
      className={textClassName}
      {...(onPress
        ? {
            ...getPressProps(onPress),
            ...getAccessibilityProps({ role: 'button', hint: 'Shows the full reference' }),
          }
        : {})}>
      {text}
    </Text>
  );
}

/** Cursor hint on web only; native ignores `cursor`. */
const CLICKABLE_STYLE = { cursor: 'pointer' } as unknown as { cursor: 'auto' };

const UNDERLINE_STYLE = {
  textDecorationLine: 'underline' as const,
  textDecorationStyle: 'solid' as const,
};
